import { useCallback, useMemo, useRef } from 'react';
import debounce from 'lodash.debounce';

/**
 * This hook is useful when you want to track mouse hover events for component as a whole.
 * When the mouse switches from A to B, the leave of A triggers before the enter of B.
 * So, by merging both of these events into a single debounced function, we can essentially ignore
 * the change of hover, assuming both A and B are using the returned enter and leave functions.
 *
 * IMPORTANT: Mapbox event objects (especially the features array) are mutated or recycled by Mapbox after the event loop.
 * If you debounce the handler and pass the original event, the features may be lost or corrupted by the time your handler runs.
 * To prevent this, we shallow-clone the event and deep-clone the features array synchronously before debouncing.
 *
 * @param onHoverStart memoized function to be called on mouse enter
 * @param onHoverEnd memoized function to be called on mouse leave
 * @param isMouseMove whether the returned onMouseEnter will be used in a mousemove event, in which case onHoverStart will be called regardless of the previous hover state
 * @param debounceTime time in ms used in the debounce function, defaults to 100
 * @returns memoized object of enter and leave functions to be used instead of the passed in functions
 */
const useGroupHoverMapBoxEvents = <T extends (event: any) => void, K extends (event: any) => void>(
  onHoverStart?: T,
  onHoverEnd?: K,
  isMouseMove = false,
  debounceTime = 100
) => {
  const isHovered = useRef(false);

  // CLONE the event synchronously before debounce!
  const cloneEvent = (event: any) => {
    if (!event) return event;
    // Shallow clone all top-level props, and deep clone features array if present
    const cloned: any = { ...event };
    if (event.features) {
      cloned.features = event.features.map((f: any) => ({ ...f }));
    }
    return cloned;
  };

  const handleHoverChange = useCallback(
    (action: 'enter' | 'leave', event: any) => {
      if (action === 'leave' && onHoverEnd) {
        onHoverEnd(event);
      } else if (action === 'enter' && onHoverStart && (!isHovered.current || isMouseMove)) {
        onHoverStart(event);
      }
      isHovered.current = action === 'enter';
    },
    [isMouseMove, onHoverEnd, onHoverStart]
  );

  // Clone event BEFORE passing to debounce!
  const debouncedHandleHoverChange = useMemo(() => debounce(handleHoverChange, debounceTime), [handleHoverChange, debounceTime]);

  const onMouseEnter = useMemo<T>(() => ((event: any) => debouncedHandleHoverChange('enter', cloneEvent(event))) as T, [debouncedHandleHoverChange]);
  const onMouseLeave = useMemo<K>(() => ((event: any) => debouncedHandleHoverChange('leave', cloneEvent(event))) as K, [debouncedHandleHoverChange]);

  return useMemo(() => {
    if (!onHoverStart && !onHoverEnd) {
      return { onMouseEnter: undefined, onMouseLeave: undefined };
    }
    return { onMouseEnter, onMouseLeave };
  }, [onHoverEnd, onHoverStart, onMouseEnter, onMouseLeave]);
};
export default useGroupHoverMapBoxEvents;
